import { UpdateDeviceCalibrationLevelInput } from '@/__generated__/graphql';
import { useModalStatusMessage } from '@/hooks';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import { CheckboxController } from '@components/controllers/CheckboxController';
import { NumericTextFieldController } from '@components/controllers/NumericTextFieldController';
import { DeviceTabProps } from '@features/deviceDataConfigurationModal/components/tabs/types';
import UPDATE_DEVICE_CALIBRATION_LEVEL from '@features/deviceDataConfigurationModal/graphql/mutations/updateDeviceCalibrationLevel';
import GET_DEVICE_LEVEL_CALIBRATION from '@features/deviceDataConfigurationModal/graphql/queries/getDeviceLevelCalibration';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  InputAdornment,
  Typography,
} from '@mui/material';
import { useEffect, type FC } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const schema = () => {
  return z.object({
    deviceId: z.string(),
    level: z.number({ invalid_type_error: 'Debes ingresar un número' }),
    inverted: z.boolean().optional(),
  }) satisfies z.ZodType<UpdateDeviceCalibrationLevelInput>;
};

type FormValues = z.infer<ReturnType<typeof schema>>;

const LevelCalibrationForm: FC<DeviceTabProps & { hasPotentiometer?: boolean }> = ({ 
  deviceId,
  onClose,
  setIsDirty,
  hasPotentiometer = false
}) => {
  const { data } = useSuspenseQuery(GET_DEVICE_LEVEL_CALIBRATION, { variables: { deviceId } });
  const { _ } = useLingui();
  const { openModalErrorMessage, openModalSuccessMessage } = useModalStatusMessage();

  const defaultValues = {
    deviceId,
    level: data.device.dataConfiguration.calibration?.level ?? undefined,
    ...(hasPotentiometer && {
      inverted: data.device.dataConfiguration.calibration?.inverted ?? false,
    }),
  };

  const methods = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(schema()),
  });

  const [update, { loading: updateLoading }] = useMutation(UPDATE_DEVICE_CALIBRATION_LEVEL, {
    onCompleted: (data) => {
      openModalSuccessMessage(_(msg`Datos actualizados correctamente`));
      const { level, inverted } = data.updateDeviceCalibrationLevel.calibration ?? {};
      methods.reset({
        deviceId,
        level: level ?? undefined,
        ...(hasPotentiometer && { inverted: inverted ?? false }),
      });
    },
    onError: (errors) => {
      openModalErrorMessage(_(msg`Error al actualizar los datos: ${errors.message}`));
    },
  });

  const { isDirty, isSubmitting } = methods.formState;

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty, setIsDirty]);


  const submitHandler = async (formData: FormValues) => {
    update({ variables: { input: formData } });
  };

  return (
    <form onSubmit={methods.handleSubmit(submitHandler)}>
      <DialogContent
        sx={{
          maxWidth: 'md',
          minHeight: 500,
          margin: 'auto',
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 0,
        }}
      >
        <Typography variant="h5" mb={2}>
          <Trans>Calibración nivel</Trans>
        </Typography>
        <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" fontSize="small" gap={2} mb={3}>
          <NumericTextFieldController
            control={methods.control}
            name="level"
            label={_(msg`Nivel de calibración`)}
            slotProps={{
              textField: {
                InputProps: {
                  endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                },
              },
            }}
          />
        </Box>
        {hasPotentiometer && (
          <>
            {/** 
             * Indicates whether the potentiometer reading should be interpreted in reverse 
             * for gate position calculation 
             */}
            <CheckboxController
              control={methods.control}
              name="inverted"
              label={<Trans>Invertido</Trans>}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ maxWidth: 'md', margin: 'auto', py: 2 }}>
        <Button
          disabled={!isDirty}
          color="info"
          variant="outlined"
          onClick={() => methods.reset(defaultValues)}
        >
          <Trans>Restablecer Formulario</Trans>
        </Button>
        <Box flexGrow={1} />
        <Button color="info" variant="outlined" onClick={() => onClose()}>
          <Trans>Cerrar</Trans>
        </Button>
        <Button
          disabled={!isDirty}
          loading={isSubmitting || updateLoading}
          type="submit"
          variant="contained"
        >
          <Trans>Guardar</Trans>
        </Button>
      </DialogActions>
    </form>
  );
};

export default LevelCalibrationForm;
